import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../api';
import { useGame } from '../context/GameContext';

const FACTORY_TYPES = [
  { key: 'refinery', label: 'Refinery', icon: '🔥' },
  { key: 'processor', label: 'Processor', icon: '💎' },
  { key: 'chemical_plant', label: 'Chemical Plant', icon: '🧪' },
  { key: 'foundry', label: 'Foundry', icon: '🏗' },
  { key: 'electronics_fab', label: 'Electronics Fab', icon: '⚡' },
  { key: 'assembly_plant', label: 'Assembly Plant', icon: '🔧' },
  { key: 'power_plant', label: 'Power Plant', icon: '⚛' },
  { key: 'shipyard', label: 'Shipyard', icon: '🚀' },
];

function fmtResource(key) {
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function ProductionPanel() {
  const { refreshInventory } = useGame();
  const [factories, setFactories] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [selectedSector, setSelectedSector] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [selectedFactory, setSelectedFactory] = useState(null);
  const [buildType, setBuildType] = useState('');
  const [craftQty, setCraftQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [costs, setCosts] = useState({ buildCosts: {}, upgradeCosts: {} });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [facRes, secRes, costsRes] = await Promise.all([
        api.getFactories(selectedSector),
        api.getSectors(),
        api.getProductionCosts(),
      ]);
      setFactories(facRes.data.factories || []);
      setSectors(secRes.data.sectors || []);
      setCosts(costsRes.data || { buildCosts: {}, upgradeCosts: {} });
    } catch (err) {
      console.error('Load production error:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedSector]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadRecipes = async (factoryType) => {
    try {
      const { data } = await api.getRecipes(factoryType);
      setRecipes(data.recipes || []);
    } catch (err) {
      console.error('Load recipes error:', err);
    }
  };

  const handleBuild = async () => {
    if (!selectedSector || !buildType) return;
    try {
      await api.buildFactory(selectedSector, buildType);
      setActionMsg('Factory built!');
      setBuildType('');
      await loadData();
      refreshInventory();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Build failed');
    }
  };

  const handleSetRecipe = async (factoryId, recipeKey) => {
    try {
      await api.setRecipe(factoryId, recipeKey);
      setActionMsg('Recipe set!');
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Set recipe failed');
    }
  };

  const handleCraft = async (factoryId) => {
    try {
      await api.startCraft(factoryId, craftQty);
      setActionMsg(`Crafting started (x${craftQty})`);
      setCraftQty(1);
      await loadData();
      refreshInventory();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Craft failed');
    }
  };

  const handleCancelJob = async (jobId) => {
    try {
      await api.cancelCraft(jobId);
      setActionMsg('Job cancelled, resources refunded.');
      await loadData();
      refreshInventory();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Cancel failed');
    }
  };

  const handleUpgrade = async (factoryId) => {
    try {
      await api.upgradeFactory(factoryId);
      setActionMsg('Factory upgraded!');
      await loadData();
      refreshInventory();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Upgrade failed');
    }
  };

  const getFactoryIcon = (type) => FACTORY_TYPES.find(ft => ft.key === type)?.icon || '⚙';

  if (loading) return <div className="panel-loading">Loading production...</div>;

  return (
    <div className="panel-container production-panel">
      <div className="panel-header">
        <h2>⚙ Production</h2>
        <div className="sector-filter">
          <label>Sector:</label>
          <select value={selectedSector || ''} onChange={e => setSelectedSector(e.target.value ? Number(e.target.value) : null)}>
            <option value="">All</option>
            {sectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      </div>

      {actionMsg && <div className="action-msg">{actionMsg}<button className="msg-dismiss" onClick={() => setActionMsg('')}>×</button></div>}

      {/* Build New Factory */}
      {selectedSector && (
        <div className="section build-section">
          <h3 className="section-title"><span className="title-accent">+</span> Build Factory</h3>
          <div className="factory-type-grid">
            {FACTORY_TYPES.map(ft => (
              <div
                key={ft.key}
                className={`factory-type-card ${buildType === ft.key ? 'selected' : ''}`}
                onClick={() => setBuildType(ft.key)}
              >
                <span className="ft-icon">{ft.icon}</span>
                <span className="ft-label">{ft.label}</span>
                {costs.buildCosts[ft.key] && (
                  <span className="ft-cost">{costs.buildCosts[ft.key].credits.toLocaleString()} ₢</span>
                )}
              </div>
            ))}
          </div>
          {buildType && (
            <div className="build-cost-hint">
              Cost: <strong>{costs.buildCosts[buildType]?.credits?.toLocaleString() ?? '…'} ₢</strong>
            </div>
          )}
          <button className="btn btn-glow" onClick={handleBuild} disabled={!buildType}>Build {buildType && FACTORY_TYPES.find(ft => ft.key === buildType)?.label}</button>
        </div>
      )}

      {/* Factory List */}
      <div className="factory-list">
        {factories.length === 0 && <p className="hud-empty">No factories. Select a sector and build one to start producing.</p>}
        {factories.map(f => (
          <div
            key={f.id}
            className={`section factory-card ${selectedFactory === f.id ? 'selected' : ''}`}
            onClick={() => {
              setSelectedFactory(f.id === selectedFactory ? null : f.id);
              loadRecipes(f.factory_type);
            }}
          >
            <div className="factory-header">
              <span className="factory-icon">{getFactoryIcon(f.factory_type)}</span>
              <div className="factory-info">
                <span className="factory-name">{f.factoryTypeName || f.factory_type}</span>
                <span className="factory-meta">Level {f.level} • {f.recipe_key ? fmtResource(f.recipe_key) : 'No recipe'}</span>
              </div>
              <div className="factory-badges">
                <span className="level-badge">Lv.{f.level}</span>
                {f.is_active && <span className="badge status-active">ACTIVE</span>}
              </div>
            </div>

            {/* Active Jobs */}
            {f.jobs && f.jobs.length > 0 && (
              <div className="job-list">
                {f.jobs.map(j => {
                  const pct = j.quantity > 0 ? Math.min(100, (j.completed / j.quantity) * 100) : 0;
                  return (
                    <div key={j.id} className="job-item">
                      <div className="job-header">
                        <span className="job-recipe">{fmtResource(j.recipe_key)}</span>
                        <span className="job-progress-text">{j.completed}/{j.quantity}</span>
                      </div>
                      <div className="progress-track">
                        <div className="progress-fill" style={{ width: `${pct}%` }} />
                      </div>
                      <button className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); handleCancelJob(j.id); }}>
                        Cancel
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Expanded Actions */}
            {selectedFactory === f.id && (
              <div className="factory-actions" onClick={e => e.stopPropagation()}>
                {/* Recipe Selector with details */}
                {recipes.length > 0 && (
                  <div className="recipe-section">
                    <h4>Available Recipes</h4>
                    <div className="recipe-grid">
                      {recipes.map(r => (
                        <div
                          key={r.key}
                          className={`recipe-card ${f.recipe_key === r.key ? 'active' : ''}`}
                          onClick={() => handleSetRecipe(f.id, r.key)}
                        >
                          <span className="recipe-name">{fmtResource(r.key)}</span>
                          <span className="recipe-layer">Layer {r.layer}</span>
                          {r.inputs && (
                            <div className="recipe-io">
                              <div className="recipe-inputs">
                                {Object.entries(r.inputs).map(([res, amt]) => (
                                  <span key={res} className="recipe-input">{amt}× {fmtResource(res)}</span>
                                ))}
                              </div>
                              <span className="recipe-arrow">→</span>
                              <div className="recipe-outputs">
                                {Object.entries(r.outputs).map(([res, amt]) => (
                                  <span key={res} className="recipe-output">{amt}× {fmtResource(res)}</span>
                                ))}
                              </div>
                              {r.craftTime && <span className="recipe-time">{r.craftTime}s/batch</span>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {f.recipe_key && (
                  <div className="craft-controls">
                    <div className="craft-row">
                      <label>Quantity:</label>
                      <input
                        type="number" min="1" max="100" value={craftQty}
                        onChange={e => setCraftQty(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                      />
                      <button className="btn btn-glow" onClick={() => handleCraft(f.id)}>Start Craft</button>
                    </div>
                  </div>
                )}

                <div className="upgrade-section">
                  <button className="btn btn-upgrade" onClick={() => handleUpgrade(f.id)}>
                    Upgrade to Lv.{f.level + 1}
                  </button>
                  <div className="upgrade-meta">
                    {costs.upgradeCosts[f.level] && (
                      <span className="upgrade-cost">{costs.upgradeCosts[f.level].toLocaleString()} ₢</span>
                    )}
                    <span className="upgrade-hint">+30% speed per level</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ProductionPanel;
